import { TranslationKey } from '@Localization';
import { injectable, interfaces } from 'inversify';
import { action, IObservableArray, makeObservable, observable, set } from 'mobx';
import { v4 } from 'uuid';
import { IQuestionModel } from '../../../annotation/question.model';
import { IImageFilters, IImageFiltersData, ImageDefaultFilters, ISavedFilter } from './imageFilters.model';
import { IImagesPagingInfo, ImagesDefaultPaging, ImageSelectionMode, ImageSetType, IProjectDetailsImage } from './projectDetailsImages.model';

export interface IProjectDetailsImagesStore {
  readonly projectId: string;
  readonly availableFilters: IImageFiltersData;
  readonly detailsImages: IObservableArray<IProjectDetailsImage>;
  readonly imagesPaging: IImagesPagingInfo;
  readonly imageFilters: IImageFilters;
  readonly filterId: string;
  readonly savedFilters: IObservableArray<ISavedFilter>;
  readonly selectionMode: ImageSelectionMode;
  readonly toggledImages: IObservableArray<string>;
  readonly lastSelectedImageId: string | undefined;
  readonly showAnswerQuestionsModal: boolean;
  readonly questions: IObservableArray<IQuestionModel>;
  readonly batchAnswerQuestionsRequestInProgress: boolean;
  readonly batchAnswerBatchesCount: number;
  readonly batchAnswerSuccessfulBatches: number;
  readonly batchAnswerFailedBatches: number;
  readonly batchAnswerQuestionsError?: TranslationKey;
  readonly batchAnswerQuestionModalSessionId?: string;
  resetStorage(projectId: string, filterId: string | undefined): void;
  setFilterData(availableImageFilters: IImageFiltersData, imageFilters: IImageFilters | null, savedFilters: ISavedFilter[]): void;
  setQuestions(imageTabQuestions: IQuestionModel[]): void;
  updateImages(images: IProjectDetailsImage[], paging: IImagesPagingInfo, filterId: string): void;
  setImagePaging(paging: IImagesPagingInfo): void;
  updateImageSetType(imageId: string, imageSetType: ImageSetType): void;
  setSelectionMode(selectionMode: ImageSelectionMode): void;
  setToggledImages(toggledImages: string[], lastSelectedId: string | undefined): void;
  setBatchAnswerQuestionsRequestInProgress(inProgress: boolean, errorCode?: TranslationKey): void;
  resetBatchAnswerQuestions(): void;
  setBatchAnswerQuestionsProgress(totalBatchesCount: number, failedCount: number, successfulCount: number): void;
  setFilters(imageFilters: IImageFilters): void;
}

export namespace IProjectDetailsImagesStore {
  export const $: interfaces.ServiceIdentifier<IProjectDetailsImagesStore> = Symbol('PROJECT_DETAILS_IMAGES_STORE');
}

@injectable()
export class ProjectDetailsImagesStore implements IProjectDetailsImagesStore {
  projectId: string = '';

  detailsImages: IObservableArray<IProjectDetailsImage> = observable.array([]);

  imagesPaging: IImagesPagingInfo = ImagesDefaultPaging;

  availableFilters: IImageFiltersData = {
    datasets: [],
    annotationTypes: [],
    questionFilters: [],
    annotatedBy: [],
    reviewedBy: [],
  };

  filterId: string = '';

  selectionMode: ImageSelectionMode = 'Select';

  lastSelectedImageId: string | undefined = undefined;

  toggledImages: IObservableArray<string> = observable.array([]);

  showAnswerQuestionsModal: boolean = false;

  questions: IObservableArray<IQuestionModel> = observable.array([]);

  batchAnswerQuestionsRequestInProgress: boolean = false;

  batchAnswerQuestionsError?: TranslationKey = undefined;

  batchAnswerQuestionModalSessionId: string = '';

  batchAnswerBatchesCount: number = 0;

  batchAnswerFailedBatches: number = 0;

  batchAnswerSuccessfulBatches: number = 0;

  imageFilters: IImageFilters = { ...ImageDefaultFilters };

  savedFilters: IObservableArray<ISavedFilter> = observable.array([]);

  constructor() {
    makeObservable(this, {
      projectId: observable,
      imagesPaging: observable,
      availableFilters: observable,
      filterId: observable,
      selectionMode: observable,
      lastSelectedImageId: observable,
      toggledImages: observable,
      showAnswerQuestionsModal: observable,
      questions: observable,
      batchAnswerQuestionsRequestInProgress: observable,
      batchAnswerQuestionsError: observable,
      batchAnswerQuestionModalSessionId: observable,
      batchAnswerBatchesCount: observable,
      batchAnswerFailedBatches: observable,
      batchAnswerSuccessfulBatches: observable,
      imageFilters: observable,
      savedFilters: observable,
      resetStorage: action,
      updateImages: action,
      setFilterData: action,
      setQuestions: action,
      updateImageSetType: action,
      setImagePaging: action,
      setSelectionMode: action,
      setToggledImages: action,
      setBatchAnswerQuestionsRequestInProgress: action,
      resetBatchAnswerQuestions: action,
      setBatchAnswerQuestionsProgress: action,
      setFilters: action,
    });
  }

  resetStorage(projectId: string, filterId: undefined): void {
    this.projectId = projectId;
    this.selectionMode = 'Select';
    this.lastSelectedImageId = undefined;
    this.toggledImages.clear();
    this.detailsImages.clear();
    this.showAnswerQuestionsModal = false;
    if (filterId !== undefined) {
      this.filterId = filterId;
    }
    this.resetBatchAnswerQuestions();
  }

  updateImages(images: IProjectDetailsImage[], paging: IImagesPagingInfo, filterId: string): void {
    this.detailsImages.replace(images);
    this.filterId = filterId;
    set(this.imagesPaging, paging);
  }

  setFilterData(availableFilters: IImageFiltersData, imageFilters: IImageFilters | null, savedFilters: ISavedFilter[]): void {
    set(this.availableFilters, availableFilters);
    this.savedFilters.replace(savedFilters);
    set(this.imageFilters, {
      ...ImageDefaultFilters,
      ...imageFilters,
      annotationTools: imageFilters?.annotationTools ?? ImageDefaultFilters.annotationTools,
      sorting: imageFilters?.sorting ?? ImageDefaultFilters.sorting,
    });
  }

  setQuestions(questions: IQuestionModel[]): void {
    this.questions.replace(questions);
  }

  updateImageSetType(imageId: string, imageSetType: ImageSetType): void {
    const images = this.detailsImages.slice();
    const imageToUpdate = images.find((i) => i.id === imageId);

    if (imageToUpdate) {
      imageToUpdate.imageSet = imageSetType;
      this.detailsImages.replace(images);
    }
  }

  setImagePaging(paging: IImagesPagingInfo): void {
    set(this.imagesPaging, paging);
  }

  setSelectionMode(selectionMode: ImageSelectionMode): void {
    this.selectionMode = selectionMode;
    this.toggledImages.clear();
  }

  setToggledImages(toggledImages: string[], lastSelectedId: string | undefined): void {
    this.toggledImages.replace(toggledImages);
    this.lastSelectedImageId = lastSelectedId;
  }

  setBatchAnswerQuestionsRequestInProgress(inProgress: boolean, errorCode?: TranslationKey): void {
    this.batchAnswerQuestionsRequestInProgress = inProgress;
    this.batchAnswerQuestionsError = errorCode;
  }

  resetBatchAnswerQuestions(): void {
    this.batchAnswerQuestionsError = undefined;
    this.batchAnswerQuestionsRequestInProgress = false;
    this.batchAnswerQuestionModalSessionId = v4();
    this.batchAnswerBatchesCount = 0;
    this.batchAnswerSuccessfulBatches = 0;
    this.batchAnswerFailedBatches = 0;
  }

  setBatchAnswerQuestionsProgress(totalBatchesCount: number, failedCount: number, successfulCount: number): void {
    this.batchAnswerBatchesCount = totalBatchesCount;
    this.batchAnswerFailedBatches = failedCount;
    this.batchAnswerSuccessfulBatches = successfulCount;
  }

  setFilters(imageFilters: IImageFilters): void {
    set(this.imageFilters, imageFilters);
  }
}
